import { Suspense, lazy, type ReactNode } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import type { Rol } from '@compranorte/contracts'
import { useSesion } from './auth/sesion'
import { Layout } from './componentes/Layout'
import { Catalogo } from './paginas/Catalogo'
import { Home } from './paginas/Home'
import { Login } from './paginas/Login'
import { ProductoDetalle } from './paginas/ProductoDetalle'
import { Recuperar } from './paginas/Recuperar'
import { Registro } from './paginas/Registro'
import { Restablecer } from './paginas/Restablecer'
import { SinAcceso } from './paginas/SinAcceso'
import { VerificarCorreo } from './paginas/VerificarCorreo'

/*
 * Los tres paneles se cargan aparte: son las pantallas más pesadas y las que
 * menos gente abre. Un cliente que entra a comprar no debería descargar el
 * panel de administración para nada.
 */
const PanelProveedor = lazy(() => import('./paginas/PanelProveedor'))
const PanelAdmin = lazy(() => import('./paginas/PanelAdmin'))
const PanelUsuarios = lazy(() => import('./paginas/PanelUsuarios'))

/*
 * Las dos pantallas satélite del panel de proveedor también van diferidas, y no
 * solo por peso: la importación arrastra el parser de CSV del dominio y la de
 * documentos la maquinaria de subida en tres pasos. Quien entra a publicar UN
 * producto no debería descargar ninguna de las dos.
 */
const ImportarCatalogo = lazy(() => import('./paginas/ImportarCatalogo'))
const DocumentosProducto = lazy(() => import('./paginas/DocumentosProducto'))

/*
 * El flujo de compra también se carga aparte, por la misma razón: vive detrás de
 * `Privada`, así que quien llega a mirar la vitrina —la mayoría de las visitas—
 * no tiene por qué descargar el carrito, el acuse ni el seguimiento. La portada
 * y el catálogo sí van en el bundle inicial: son lo primero que se ve.
 */
const Carrito = lazy(() => import('./paginas/Carrito'))
/* La administración de cuentas la abre una minoría: no va en el bundle inicial. */
const CuentasEmpresa = lazy(() => import('./paginas/CuentasEmpresa'))
const Confirmacion = lazy(() => import('./paginas/Confirmacion'))
const MisOrdenes = lazy(() => import('./paginas/MisOrdenes'))
const OrdenDetalle = lazy(() => import('./paginas/OrdenDetalle'))

const EQUIPO: Rol[] = ['admin', 'operador']
const PROVEEDOR_O_EQUIPO: Rol[] = ['proveedor', 'admin', 'operador']

function Cargando() {
  return (
    <div className="min-h-[40vh] grid place-items-center">
      <p className="text-[13px] text-acero-500">Cargando…</p>
    </div>
  )
}

/**
 * Puerta de una ruta que exige sesión.
 *
 * Esconder una ruta no es seguridad: quien conozca la URL puede escribirla, y
 * por eso la capa de alcance del backend responde 404 al inquilino ajeno sin
 * consultar al frontend. Esto solo evita mostrar una pantalla rota a quien
 * todavía no entró, y recuerda a dónde quería ir para llevarlo ahí después.
 */
function Privada({ children, roles }: { children: ReactNode; roles?: Rol[] }) {
  const { yo, cargando, suspendida } = useSesion()
  const lugar = useLocation()

  if (cargando) return <Cargando />

  /*
   * La suspensión va ANTES del redirect a /entrar. Si no, quien fue suspendido
   * caería en el login, entraría bien —sus credenciales siguen siendo válidas—
   * y volvería a caer acá, en un bucle sin explicación. Hay que decírselo.
   */
  if (suspendida) return <SinAcceso />

  if (!yo) return <Navigate to="/entrar" state={{ volverA: lugar.pathname }} replace />
  if (roles && !roles.includes(yo.rol)) return <SinAcceso />
  return <>{children}</>
}

/**
 * La vitrina no es para el proveedor.
 *
 * Un proveedor entra a publicar y a despachar, no a comprar. Dejarle el catálogo
 * delante hacía que la plataforma se sintiera de cliente y le mostraba una
 * vitrina donde su propio producto aparece con el PRECIO DE VENTA —el costo más
 * nuestro margen—, que es precisamente la cifra que no le corresponde ver: la
 * capa de alcance se ocupa de que el margen no viaje, pero el precio publicado
 * sí es público, y de él y de su costo el margen se despeja restando.
 *
 * Se redirige en vez de mostrar «sin acceso»: no es un permiso que le falte, es
 * una pantalla que no le sirve, y llevarlo a la suya es la respuesta útil. El
 * `replace` evita dejar la vitrina en el historial, que convertiría el botón de
 * atrás en un rebote.
 *
 * El EQUIPO conserva el catálogo: lo necesita para emitir a nombre de un cliente
 * y para revisar cómo se ve lo que publica cada proveedor.
 */
function SinVitrina({ children }: { children: ReactNode }) {
  const { yo, cargando } = useSesion()

  // Mientras carga el perfil no se decide nada: un visitante anónimo y un
  // proveedor se ven igual durante ese instante, y redirigir por adelantado
  // sacaría de la vitrina a quien sí puede verla.
  if (cargando) return <Cargando />
  if (yo?.rol === 'proveedor') return <Navigate to="/proveedor" replace />
  return <>{children}</>
}

function Rutas() {
  return (
    <Suspense fallback={<Cargando />}>
      <Routes>
        {/* Con la franja de marca y las pestañas. La vitrina se ve sin sesión. */}
        <Route element={<Layout />}>
          <Route
            path="/"
            element={
              <SinVitrina>
                <Home />
              </SinVitrina>
            }
          />
          <Route
            path="/catalogo"
            element={
              <SinVitrina>
                <Catalogo />
              </SinVitrina>
            }
          />
          <Route
            path="/producto/:id"
            element={
              <SinVitrina>
                <ProductoDetalle />
              </SinVitrina>
            }
          />

          {/* Comprar sí exige identidad. */}
          <Route
            path="/carrito"
            element={
              <Privada>
                <Carrito />
              </Privada>
            }
          />
          <Route
            path="/orden/:id/confirmacion"
            element={
              <Privada>
                <Confirmacion />
              </Privada>
            }
          />
          <Route
            path="/mis-ordenes"
            element={
              <Privada>
                <MisOrdenes />
              </Privada>
            }
          />
          <Route
            path="/mis-ordenes/:id"
            element={
              <Privada>
                <OrdenDetalle />
              </Privada>
            }
          />

          {/*
            Tres rutas al mismo panel, una por trabajo.
            
            Antes eran cuatro pestañas dentro de `/proveedor`, así que publicar
            un producto, atender una orden y revisar cuándo se cobra vivían todas
            bajo el rótulo «Mis productos» y solo se descubrían entrando. Cada
            una tiene ahora su entrada en la barra y su propia URL: se puede
            compartir el enlace de las órdenes y volver con el botón de atrás.
          */}
          {/*
            La administración de cuentas de la propia empresa. UNA ruta para
            clientes y proveedores: la lógica es idéntica —cuentas de una empresa
            y tres niveles— y lo único que cambia es cómo se llama el nivel del
            medio. Dos rutas habrían significado dos copias de la misma regla.

            `Privada` solo comprueba que haya sesión; el nivel lo comprueba la
            pantalla, y el backend responde 403 de todos modos. Esconder la
            entrada es una cortesía, no el control.
          */}
          <Route
            path="/empresa/cuentas"
            element={
              <Privada>
                <CuentasEmpresa />
              </Privada>
            }
          />

          <Route
            path="/proveedor"
            element={
              <Privada roles={PROVEEDOR_O_EQUIPO}>
                <PanelProveedor seccion="productos" />
              </Privada>
            }
          />
          <Route
            path="/proveedor/ordenes"
            element={
              <Privada roles={PROVEEDOR_O_EQUIPO}>
                <PanelProveedor seccion="ordenes" />
              </Privada>
            }
          />
          <Route
            path="/proveedor/pagos"
            element={
              <Privada roles={PROVEEDOR_O_EQUIPO}>
                <PanelProveedor seccion="pagos" />
              </Privada>
            }
          />
          <Route
            path="/proveedor/catalogo"
            element={
              <Privada roles={PROVEEDOR_O_EQUIPO}>
                <ImportarCatalogo />
              </Privada>
            }
          />
          <Route
            path="/proveedor/producto/:id/documentos"
            element={
              <Privada roles={PROVEEDOR_O_EQUIPO}>
                <DocumentosProducto />
              </Privada>
            }
          />

          <Route
            path="/admin"
            element={
              <Privada roles={EQUIPO}>
                <PanelAdmin />
              </Privada>
            }
          />
          <Route
            path="/admin/usuarios"
            element={
              <Privada roles={['admin']}>
                <PanelUsuarios />
              </Privada>
            }
          />
        </Route>

        {/* Fuera del Layout: pantallas de acceso, a pantalla completa. */}
        <Route path="/entrar" element={<Login />} />
        <Route path="/registro" element={<Registro />} />
        <Route path="/verificar-correo" element={<VerificarCorreo />} />
        <Route path="/recuperar" element={<Recuperar />} />
        <Route path="/restablecer" element={<Restablecer />} />

        {/* Una URL escrita a mano que no existe vuelve a la portada. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}

/**
 * `BrowserRouter` y no `HashRouter`.
 *
 * Los enlaces de los correos —verificación y recuperación— los construye el
 * backend como `${APP_URL}/verificar-correo?token=…`, que con rutas de hash
 * simplemente no funcionan. Además el servidor ya sirve `index.html` para
 * cualquier ruta desconocida, que es lo único que hacía falta.
 */
export function App() {
  return (
    <BrowserRouter>
      <Rutas />
    </BrowserRouter>
  )
}
